// import debug from "../debug";
import { Labels } from "../types";
import { BoardState, TimelineEvent } from "./events";

// const log = debug("treeFunnel");

export type FunnelItem = {
    id: string;
    visitedLabels: string[];
    visitedLevels: number[];
};

export type TreeFunnelNode = {
    level: number;
    label: string;
    count: number; // count of items
    weight: number; // sum of weights of items
    order: number; // order within the same level of the tree
    top: number; // relative sum of all "count" values for previous nodes in this level, useful for positioning when nodes are scaled based on number of items in them
    height: number;
    out: { [label: string]: { count: number; weight: number; level: number; height: number } };
    in: { [label: string]: { count: number; weight: number; level: number; height: number } };
    items: FunnelItem[]; // items which visited this node

    compressible?: boolean; // used during processing - compress stage
    normalisedWeight: number;
};

export type TreeFunnel = { [label: string]: TreeFunnelNode }[];
export type TreeFunnelInfo = { rescaledNodeHeight: boolean };

type TreeFunnelParams = {
    centerVertically?: boolean; // set top offset to center vertically
    scaleBasedOnCount?: boolean; // whether nodes should have size relative to count of items which visited them
    firstStatus?: string; // if set, first status has to be hit to start tracking
    lastStatus?: string; // if set, first hit of this status ends tracking
    trackedStatuses?: Set<string>; // if set, any other statuses will be ignored and won't be shown on a graph
    mergeStatuses?: boolean; // if true, merge last statuses when possible, so that there's less nodes with the same label
    weightColumnId?: string;
    filterNodeWeightLessThan?: number;
    filterLevelsAbove?: number;
    filterItemsOut?: Set<string>;
    maxNodeHeightRatio?: number;
};

export function treeFunnel(
    events: TimelineEvent[],
    columnId: string,
    labels: Labels,
    board: BoardState,
    params: TreeFunnelParams
): { funnel: TreeFunnel; info: TreeFunnelInfo } {
    const {
        centerVertically,
        scaleBasedOnCount,
        firstStatus,
        lastStatus,
        trackedStatuses,
        mergeStatuses,
        weightColumnId,
        filterNodeWeightLessThan,
        filterLevelsAbove,
        filterItemsOut,
        maxNodeHeightRatio
    } = params;
    const { items: boardItems } = board;
    const lastNodePerItem: { [id: string]: TreeFunnelNode } = {};
    const trackingStarted: Set<string> = new Set(); // only used if firstStatus is set
    const trackingEnded: Set<string> = new Set(); // only used if lastStatus is set
    let levels: { [label: string]: TreeFunnelNode }[] = [];
    const info: TreeFunnelInfo = {
        rescaledNodeHeight: false
    };

    // add first/last status to set of tracked for simpler checks later on
    if (trackedStatuses !== undefined) {
        if (firstStatus !== undefined) {
            trackedStatuses.add(firstStatus);
        }
        if (lastStatus !== undefined) {
            trackedStatuses.add(lastStatus);
        }
    }

    const funnelItems: { [id: string]: FunnelItem } = {};
    const getFunnelItem = (id: string, newLabel: string, level: number): FunnelItem => {
        let result: FunnelItem;
        if (funnelItems[id] === undefined) {
            result = { id, visitedLabels: [newLabel], visitedLevels: [level] };
            funnelItems[id] = result;
        } else {
            result = funnelItems[id];
            result.visitedLabels.push(newLabel);
            result.visitedLevels.push(level);
        }
        return result;
    };

    // build levels with funnel nodes
    events.forEach((event) => {
        if (event.column === columnId && !(filterItemsOut && filterItemsOut.has(event.item))) {
            //
            // skip tracking in some cases if we hit ignored statuses
            //
            if (trackingEnded.has(event.item + "")) {
                return;
            }

            if (firstStatus !== undefined) {
                // eslint-disable-next-line eqeqeq
                if (!trackingStarted.has(event.item + "") && firstStatus == event.value) {
                    trackingStarted.add(event.item);
                }
                if (!trackingStarted.has(event.item)) {
                    return;
                }
            }

            if (lastStatus !== undefined) {
                // eslint-disable-next-line eqeqeq
                if (lastStatus == event.value) {
                    trackingEnded.add(event.item);
                }
            }

            if (trackedStatuses !== undefined && !trackedStatuses.has(event.value + "")) {
                return;
            }

            //
            // update funnel node
            //

            const lastNode = lastNodePerItem[event.item];
            const level = lastNode ? lastNode.level + 1 : 0;
            if (levels.length <= level) {
                levels.push({});
            }
            if (levels[level][event.value] === undefined) {
                levels[level][event.value] = {
                    level,
                    label: event.value,
                    order: labels.order[event.value],
                    count: 0,
                    weight: 0,
                    normalisedWeight: 0,
                    top: 0,
                    height: 0,
                    out: {},
                    in: {},
                    items: []
                };
            }
            const newNode = levels[level][event.value];
            newNode.count += 1;
            const v = weightColumnId ? boardItems[event.item][weightColumnId] : 1;
            const weight = v !== undefined && v !== "" && !isNaN(v) ? parseFloat(v) : 1;
            newNode.weight += weight;

            newNode.items.push(getFunnelItem(event.item, event.value, level));

            if (lastNode) {
                if (newNode.in[lastNode.label] === undefined) {
                    newNode.in[lastNode.label] = { count: 1, weight, level: lastNode.level, height: 0 };
                } else {
                    newNode.in[lastNode.label].count += 1;
                    newNode.in[lastNode.label].weight += weight;
                }

                if (lastNode.out[event.value] === undefined) {
                    lastNode.out[event.value] = { count: 1, weight, level, height: 0 };
                } else {
                    lastNode.out[event.value].count += 1;
                    lastNode.out[event.value].weight += weight;
                }
            }
            lastNodePerItem[event.item] = newNode;
        }
    });

    if (mergeStatuses) {
        levels = compress(levels);
    }

    if (filterNodeWeightLessThan) {
        console.error("not implemented");
    }

    levels = sortNodes(levels);

    if (filterLevelsAbove !== undefined) {
        let visibleLevels = levels.slice(0, filterLevelsAbove);
        // only scale levels which are going to be visible
        visibleLevels = scaleNodes(
            visibleLevels,
            info,
            maxNodeHeightRatio || 1e10,
            scaleBasedOnCount,
            centerVertically
        );
        levels = [...visibleLevels, ...levels.slice(filterLevelsAbove)];
    } else {
        levels = scaleNodes(levels, info, maxNodeHeightRatio || 1e10, scaleBasedOnCount, centerVertically);
    }

    return { funnel: levels, info };
}

function sortNodes(funnel: TreeFunnel): TreeFunnel {
    funnel.forEach((level, idx) => {
        const sortedLabels = Object.keys(level).sort((a, b) => level[a].order - level[b].order);
        sortedLabels.forEach((label, nodeIdx) => {
            level[label].order = nodeIdx;
        });
    });
    return funnel;
}

function scaleNodes(
    funnel: TreeFunnel,
    info: TreeFunnelInfo,
    maxNodeHeightRatio: number,
    scaleBasedOnCount?: boolean,
    centerVertically?: boolean
): TreeFunnel {
    //
    // rebalance weights, so that discrepancies are not that high
    //
    let minWeight = 1e15,
        maxWeight = 0;

    funnel.forEach((level) => {
        const levelWeights = Object.values(level)
            .filter((node) => node.weight !== 0)
            .map((node) => node.weight);
        maxWeight = Math.max(maxWeight, ...levelWeights);
        minWeight = Math.min(minWeight, ...levelWeights);
    });
    const ratio = minWeight === 0 ? maxWeight : maxWeight / minWeight;
    let normalisationFunc: (weight: number) => number;

    if (ratio > maxNodeHeightRatio) {
        info.rescaledNodeHeight = true;
        const newMaxWeight = minWeight * maxNodeHeightRatio;

        normalisationFunc = (weight: number): number =>
            minWeight + (weight - minWeight) * ((newMaxWeight - minWeight) / (maxWeight - minWeight));
    } else {
        normalisationFunc = (weight): number => weight;
    }

    funnel.forEach((level) => {
        Object.values(level).forEach((node) => (node.normalisedWeight = normalisationFunc(node.weight || minWeight)));
    });

    //
    // calculate top offsets and heights of nodes
    //

    let weightPerLevel: number[];
    if (scaleBasedOnCount) {
        weightPerLevel = funnel.map((level) =>
            Object.values(level).reduce((acc, node) => acc + node.normalisedWeight!, 0)
        );
    } else {
        weightPerLevel = funnel.map((level) => Object.values(level).length);
    }

    const maxWeightPerLevel = Math.max(...weightPerLevel);

    if (scaleBasedOnCount) {
        // calc height and top for nodes
        funnel.forEach((level, idx) => {
            const sumWeight = weightPerLevel[idx];
            // position nodes in the center
            const topOffset = centerVertically ? (maxWeightPerLevel - sumWeight) * 0.5 : 0;
            let acc = 0;
            Object.values(level)
                .sort((a, b) => a.order - b.order)
                .forEach((node) => {
                    node.top = (acc + topOffset) / maxWeightPerLevel;
                    node.height = node.normalisedWeight! / maxWeightPerLevel;
                    acc += node.normalisedWeight!;
                });
        });

        // calc height for in/out links as proportion of node heights
        funnel.forEach((level) => {
            Object.values(level).forEach((node) => {
                if (node.weight !== 0) {
                    Object.values(node.out).forEach((out) => {
                        out.height = node.height * (out.weight / node.weight);
                    });
                    Object.values(node.in).forEach((inObject) => {
                        inObject.height = node.height * (inObject.weight / node.weight);
                    });
                }
            });
        });
    } else {
        funnel.forEach((level, idx) => {
            Object.values(level).forEach((node, nodeIdx) => {
                node.top = node.order / weightPerLevel[idx];
                node.height = 1.0 / maxWeightPerLevel;
            });
        });
    }

    return funnel;
}

/**
 * reduce number of nodes if all levels end up with the same node
 * @param funnel
 */
function compress(funnel: TreeFunnel): TreeFunnel {
    let reducedPaths = 0;
    do {
        const reductionPaths: { [key: string]: number[] } = {};
        for (let level = funnel.length - 1; level >= 0; level--) {
            const nodes = Object.values(funnel[level]);
            for (let j = 0; j < nodes.length; j++) {
                const node = nodes[j];
                const outCount = Object.keys(node.out).length;
                // todo: when outCount === 1 check removed algo breaks as too many nodes are being deleted at once
                if (outCount === 0 || (outCount === 1 && node.compressible)) {
                    // no out or 1 out leading to previously compressed label
                    if (reductionPaths[node.label] === undefined) {
                        reductionPaths[node.label] = [level];
                    } else {
                        reductionPaths[node.label].push(level);
                    }
                }
            }
        }

        // update funnel - add all "out" to the node in the highest level, remove nodes in the lower levels
        Object.keys(reductionPaths)
            .filter((label) => reductionPaths[label].length > 1)
            .forEach((label) => {
                const reductionPath = reductionPaths[label];
                const highestLevel = reductionPath[0];
                const accumulatedNode: TreeFunnelNode = funnel[highestLevel][label];
                for (let i = 1; i < reductionPath.length; i++) {
                    const nodeToDelete = funnel[reductionPath[i]][label];
                    accumulatedNode.count += nodeToDelete.count;
                    accumulatedNode.weight += nodeToDelete.weight;
                    accumulatedNode.items.push(...nodeToDelete.items);

                    // transfer all "out" to node which is accumulating
                    Object.keys(nodeToDelete.out).forEach((outLabel) => {
                        if (accumulatedNode.out[outLabel]) {
                            accumulatedNode.out[outLabel].count += nodeToDelete.out[outLabel].count;
                            accumulatedNode.out[outLabel].weight += nodeToDelete.out[outLabel].weight;
                        } else {
                            accumulatedNode.out[outLabel] = { ...nodeToDelete.out[outLabel] };
                        }
                    });

                    // move 'out' of nodes which point to the one being deleted
                    Object.keys(nodeToDelete.in).forEach((inLabel) => {
                        const nodeToModify: TreeFunnelNode = funnel[nodeToDelete.in[inLabel].level][inLabel];
                        // if (!nodeToModify) {
                        //     console.error(
                        //         `missing node to modify label ${inLabel}, values ${JSON.stringify(
                        //             nodeToDelete.in[inLabel]
                        //         )}, referenced from node ${JSON.stringify(nodeToDelete)}`
                        //     );
                        //     return;
                        // }
                        nodeToModify.out[label].level = accumulatedNode.level;
                        nodeToModify.compressible = true;
                        if (accumulatedNode.in[inLabel]) {
                            accumulatedNode.in[inLabel].count += nodeToDelete.in[inLabel].count;
                            accumulatedNode.in[inLabel].weight += nodeToDelete.in[inLabel].weight;
                        } else {
                            accumulatedNode.in[inLabel] = { ...nodeToDelete.in[inLabel] };
                        }
                        // console.log("node marked compressible ", nodeToModify);
                    });

                    // console.log(`deleting label ${label}, level ${reductionPath[i]}, node: ${JSON.stringify(funnel[reductionPath[i]][label])}`);
                    delete funnel[reductionPath[i]][label];
                }

                Object.keys(accumulatedNode.in).forEach((inLabel) => {
                    funnel[accumulatedNode.in[inLabel].level][inLabel].compressible = true;
                });
            });

        reducedPaths = Object.values(reductionPaths).reduce((acc, path) => acc + (path.length > 1 ? 1 : 0), 0);
        // console.log(`reduced (${reducedPaths}): ${JSON.stringify(reductionPaths)}`);
        // todo: algorithm broken
        break;
    } while (reducedPaths > 0);

    return funnel;
}
